import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { setFormArrayLength } from '@core/utils/set-form-array-length';
import {
  TravelerAllowedRange,
  TravelerGroup,
} from '@dynamic-components/booker-v2/shared/traveler-selector/types';
import { BOOKER_CONFIG, TravelerSelector } from '@shared/booker';
import { equals } from 'ramda';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';

@Component({
  selector: 'dot-traveler-group',
  templateUrl: './traveler-group.component.html',
  styleUrls: ['./traveler-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TravelerGroupComponent,
      multi: true,
    },
  ],
})
export class TravelerGroupComponent implements ControlValueAccessor, OnInit {
  @Input() content: TravelerSelector;
  @Input() travelerAllowedRange: TravelerAllowedRange;
  @Input() requireChildAges: boolean;

  private _destroy = inject(DestroyRef);
  private _onChange: (_: any) => void;

  readonly childAgesControl = this.fb.array<number>([]);

  readonly formGroup = this.fb.group({
    adults: 1,
    children: 0,
    infants: 0,
    childAges: this.childAgesControl,
  });

  readonly allowedAges: number[] = Array.from(
    Array(BOOKER_CONFIG.PAX.MAX_MINOR_AGE).keys(),
  ).slice(BOOKER_CONFIG.PAX.MIN_MINOR_AGE);

  childAgeLabels: string[] = [];

  constructor(private fb: FormBuilder) {
    this.formGroup.controls.children.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(childrenCount => {
        this.setChildAgesFormArrayLength(childrenCount);
        this.getChildAgeDropdownLabels(childrenCount);
      });
  }

  ngOnInit(): void {
    this.formGroup.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroy),
        distinctUntilChanged((a, b) => equals(a, b)),
      )
      .subscribe(value => {
        if (this._onChange) {
          this._onChange(value);
        }
      });
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(_: any): void {
    return;
  }

  writeValue(value: TravelerGroup): void {
    this.setChildAgesFormArrayLength(value.childAges?.length);
    let { childAges, ...rest } = value;
    if (!value.childAges) {
      childAges = Array(value.children).fill(null); // filling null instead of empty array to avoid NG01002 error
    }
    this.formGroup.setValue({
      ...rest,
      childAges,
    });
  }

  getChildAgeDropdownLabels(childrenCount) {
    this.childAgeLabels = Array.from(Array(childrenCount).keys()).map(index =>
      this.content.age.replace('[CHILD_NUMBER]', `${index + 1}`),
    );
  }

  setChildAgesFormArrayLength(children) {
    if (this.requireChildAges) {
      setFormArrayLength(children, this.childAgesControl, () =>
        this.fb.control<number>(undefined),
      );
    }
  }

  selectClicked($event: any) {
    // this prevents the click event from bubbling up to the travelerSelector flyout which would
    // cause the flyout to close itself when selecting the age
    $event.stopImmediatePropagation();
  }
}
